import i18n, { StringMap } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { locale, addLocale } from 'primereact/api';
import { LANGUAGE_FLAG_MAP } from 'components/_new/CountryFlag/CountryFlag';
import HttpBackend from 'i18next-http-backend';
import primereactLocaleDe from 'primelocale/de.json';
import primereactLocaleCs from 'primelocale/cs.json';
import primereactLocalePl from 'primelocale/pl.json';
import primereactLocaleEn from 'primelocale/en.json';
import { dayjs } from './utils/dayjs';
import { capitalizeWord } from './utils/helpers';

const VERSION = 11;

addLocale('en', primereactLocaleEn.en);
addLocale('de', primereactLocaleDe.de);
addLocale('pl', primereactLocalePl.pl);
addLocale('cs', primereactLocaleCs.cs);

const resources = {
	en: {},
	pl: {},
	de: {},
	cs: {},
} as const;

export const availableLanguages = Object.keys(resources);

export const getLanguageFromLocale = (localeCode: string) => {
	try {
		const locale = new Intl.Locale(localeCode);
		const { language } = locale;
		return language;
	} catch (e) {
		return localeCode.split('-')[0];
	}
};

export const getLanguageName = (languageCode: string, inLanguageCode?: string) => {
	try {
		const displayNames = new Intl.DisplayNames([inLanguageCode || languageCode], { type: 'language' });
		return displayNames.of(languageCode);
	} catch (e) {
		// Simple fallback if Intl.DisplayNames is not supported
		return languageCode;
	}
};

const supportedLngs = ['pl', 'en', 'de', 'cs'];
export const languageOptions = supportedLngs.map((lngCode) => {
	const language = lngCode; // We're already using language codes
	const languageName = getLanguageName(language) || language;
	const label = capitalizeWord(languageName);

	return {
		value: language,
		label,
		Flag: LANGUAGE_FLAG_MAP[lngCode as keyof typeof LANGUAGE_FLAG_MAP] || null,
	};
});

const changeLanguage = (localeCode: string) => {
	// Extract base language for dayjs if locale contains region
	const baseLanguage = getLanguageFromLocale(localeCode);

	if (dayjs.Ls[baseLanguage]) {
		// change date js locale
		dayjs.locale(baseLanguage);
		locale(baseLanguage);

		// change html lang tag value
		document.documentElement.setAttribute('lang', localeCode);
	} else {
		// change date js locale
		dayjs.locale('en');
		locale('en');

		// change html lang tag value
		document.documentElement.setAttribute('lang', 'en-US');
	}
};

export const fixedT = (lng: string, translation: string, options?: StringMap) =>
	i18n.getFixedT(lng)(translation, options);

i18n.use(initReactI18next)
	.use(LanguageDetector)
	.use(HttpBackend)
	.init({
		// resources,
		defaultNS: 'common',
		ns: ['common'],
		fallbackLng: 'en', // TODO switch to en when translations are completely ready
		supportedLngs,
		interpolation: {
			escapeValue: false,
		},
		nonExplicitSupportedLngs: true,
		detection: { order: ['localStorage', 'navigator'] },
		react: {
			useSuspense: true,
			transSupportBasicHtmlNodes: true,
			transKeepBasicHtmlNodesFor: ['br', 'strong', 'b', 'i'],
		},
		backend: {
			loadPath: '/static/translations/{{ns}}.{{lng}}.json',
			queryStringParams: { v: VERSION },
		},
	})
	.then(() => {
		changeLanguage(i18n.language);
	});

i18n.on('languageChanged', changeLanguage);

export default i18n;
